import { useEffect, useState } from 'react';
import { getSiteSettings, type SocialLinks } from '../../services/settings';

const LINKTREE_URL = 'https://linktr.ee/synccoreinnovations';
const WHATSAPP_URL = 'https://wa.me/2349074202400';

const exploreLinks = [
  { label: 'Marketplace', href: '/marketplace' },
  { label: 'Hostels', href: '/hostels' },
  { label: 'Sell an item', href: '/sell' },
  { label: 'Tutorials', href: '/tutorials' },
  { label: 'Referrals', href: '/referrals' },
];

// Platform → icon + how to build the href. Order defines display order.
const SOCIAL_META: { key: keyof SocialLinks; label: string; icon: string; href: (v: string) => string }[] = [
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', href: (v) => v },
  { key: 'twitter', label: 'X (Twitter)', icon: 'ri-twitter-x-line', href: (v) => v },
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line', href: (v) => v },
  { key: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line', href: (v) => v },
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-line', href: (v) => v },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line', href: (v) => v },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'ri-whatsapp-line', href: (v) => (/^https?:|^mailto:|^tel:/.test(v) ? v : `https://wa.me/${v.replace(/[^0-9]/g, '')}`) },
];

const DEFAULT_SOCIALS = [
  { label: 'WhatsApp', href: WHATSAPP_URL, icon: 'ri-whatsapp-line' },
  { label: 'Email', href: 'mailto:hello@synccore.com', icon: 'ri-mail-line' },
  { label: 'More links', href: LINKTREE_URL, icon: 'ri-links-line' },
];

const companyLinks = [
  { label: 'Support', href: '/support' },
  { label: 'Terms', href: '/terms' },
  { label: 'Privacy', href: '/privacy' },
  { label: 'Acceptable Use', href: '/acceptable-use' },
  { label: 'Refund Policy', href: '/refund-policy' },
];

const Footer = () => {
  const [socials, setSocials] = useState(DEFAULT_SOCIALS);

  useEffect(() => {
    (async () => {
      try {
        const s = await getSiteSettings();
        const configured = SOCIAL_META
          .filter((m) => (s.social?.[m.key] || '').trim())
          .map((m) => ({ label: m.label, href: m.href((s.social![m.key] as string).trim()), icon: m.icon }));
        // Show admin-configured links if any exist; keep Email as a constant contact.
        if (configured.length) {
          setSocials([...configured, { label: 'Email', href: 'mailto:hello@synccore.com', icon: 'ri-mail-line' }]);
        }
      } catch { /* keep defaults */ }
    })();
  }, []);

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 gap-y-10 gap-x-8 lg:grid-cols-12 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-5">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 shadow-sm">
                <i className="ri-graduation-cap-fill text-white text-xl"></i>
              </div>
              <div className="leading-none">
                <span className="block text-2xl font-extrabold tracking-tight">Camp<span className="text-teal-400">ket</span></span>
                <span className="block text-[10px] font-medium uppercase tracking-[0.2em] text-gray-400">Campus Market</span>
              </div>
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-gray-400">
              Buy, sell, and trade with fellow students. Instant price checks, trusted sellers, and an AI Campus Guru by your side.
            </p>
            <h4 className="mt-6 text-xs font-semibold uppercase tracking-wider text-gray-400">Follow us</h4>
            <div className="mt-3 flex items-center gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/5 text-gray-300 ring-1 ring-white/10 transition-colors hover:bg-teal-500 hover:text-white hover:ring-teal-500"
                >
                  <i className={`${s.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <nav className="lg:col-span-2" aria-label="Explore">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">Explore</h4>
            <ul className="space-y-3 text-sm">
              {exploreLinks.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-gray-400 transition-colors hover:text-teal-400">{l.label}</a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company */}
          <nav className="lg:col-span-2" aria-label="Company">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">Company</h4>
            <ul className="space-y-3 text-sm">
              {companyLinks.map((l) => (
                <li key={l.href}>
                  <a href={l.href} className="text-gray-400 transition-colors hover:text-teal-400">{l.label}</a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div className="col-span-2 lg:col-span-3">
            <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <i className="ri-phone-line mt-0.5 text-teal-400"></i>
                <span className="text-gray-400">
                  <a href="tel:09074202400" className="block transition-colors hover:text-teal-400">0907 420 2400</a>
                  <a href="tel:09016862310" className="block transition-colors hover:text-teal-400">0901 686 2310</a>
                </span>
              </li>
              <li className="flex items-center gap-3">
                <i className="ri-mail-line text-teal-400"></i>
                <a href="mailto:hello@synccore.com" className="text-gray-400 transition-colors hover:text-teal-400">hello@synccore.com</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-between gap-3 px-4 py-6 text-xs text-gray-500 sm:flex-row sm:px-6 lg:px-8">
          <p className="text-center sm:text-left">
            © {new Date().getFullYear()} Campus Market · Built by{' '}
            <a href={LINKTREE_URL} target="_blank" rel="noopener noreferrer" className="text-gray-400 transition-colors hover:text-teal-400">
              SyncCore Innovations
            </a>
          </p>
          <div className="flex items-center gap-5">
            <a href="/privacy" className="transition-colors hover:text-teal-400">Privacy</a>
            <a href="/terms" className="transition-colors hover:text-teal-400">Terms</a>
            <a href="/refund-policy" className="transition-colors hover:text-teal-400">Refunds</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
